import { request } from '@/shared/utils/request';

// GET /api/dsh/app-version/latest (public) - the newest logged release.
export interface AppVersionLatestData {
  app_id: number;
  app_name: string;
  latest_version: string;
  released_at: string | null;
  download_url: string;
  // Set once a release has an APK on record; null for rows logged by hand
  // before uploads existed.
  apk_url: string | null;
  apk_file_name: string | null;
  apk_file_size: number | null;
  apk_sha256: string | null;
  release_notes: string | null;
  current_version: string | null;
  is_outdated: boolean | null;
}

export interface AppVersionLatestResponse {
  code: string;
  message: string;
  data: AppVersionLatestData | null;
}

// GET /api/dsh/app-version/list - release history, newest first.
export interface AppVersionListItem {
  app_id: number;
  app_version: string;
  released_at: string | null;
  has_apk: boolean;
  apk_file_name: string | null;
  apk_file_size: number | null;
  apk_sha256: string | null;
  release_notes: string | null;
  registered_by: string | null;
  is_latest: boolean;
}

export interface AppVersionListResponse {
  code: string;
  message: string;
  data: {
    app_name: string;
    total: number;
    items: AppVersionListItem[];
  };
}

// POST /api/dsh/app-version (multipart: apk, app_version, release_notes?)
export interface AppVersionUploadData {
  app_id: number;
  app_name: string;
  app_version: string;
  released_at: string | null;
  apk_file_name: string;
  apk_file_size: number;
  apk_sha256: string;
  release_notes: string | null;
}

export interface AppVersionUploadResponse {
  code: string;
  message: string;
  data: AppVersionUploadData | null;
}

export interface AppVersionDeleteResponse {
  code: string;
  message: string;
  data: { app_id: number; deleted: boolean };
}

export const appVersionApi = {
  getLatest: async (): Promise<AppVersionLatestResponse> =>
    request.get<AppVersionLatestResponse>('/app-version/latest'),

  getList: async (): Promise<AppVersionListResponse> =>
    request.get<AppVersionListResponse>('/app-version/list'),

  // Stores the APK in SeaweedFS and appends a release row; the newest row is
  // what /latest and /download/latest serve from then on.
  upload: async (formData: FormData): Promise<AppVersionUploadResponse> =>
    request.post<AppVersionUploadResponse>('/app-version', formData),

  // PUT /api/dsh/app-version/{app_id} (multipart: app_version, release_notes,
  // apk optional - attach only to replace the stored build)
  update: async (appId: number, formData: FormData): Promise<AppVersionUploadResponse> =>
    request.put<AppVersionUploadResponse>(`/app-version/${appId}`, formData),

  // DELETE /api/dsh/app-version/{app_id} - removes the row and its APK
  delete: async (appId: number): Promise<AppVersionDeleteResponse> =>
    request.delete<AppVersionDeleteResponse>(`/app-version/${appId}`),
};
